/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { Box, Button, ButtonProps, Group, Menu, Text } from '@mantine/core';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import { ActionSignature, useResourceActions } from '@helpers/file';
import { isMacOS } from '@helpers/platform';
import ShortcutKbd from '@ui-kit/atoms/ShortcutKbd';

import { ActionBarProps } from './types';
import { useTranslation } from 'react-i18next';
import {
  IconDotsVertical as IconSecondaryActions,
  IconListNumbers as IconReindex,
  IconColumns,
  IconEyeOff,
  IconEye,
  IconClipboard,
} from '@tabler/icons-react';

import useAccount from '@features/Account/store/useAccount';
import { palette, surfaces } from '@ui-kit/colors';
import {
  ListResourcesDocument,
  ListResourcesQueryVariables,
  Permission,
  SortDirection,
  useReindexWorkspaceMutation,
} from '@app-types/blackroom-api';
import { useCallback } from 'react';
import invokePromptConfirm from '@features/Prompt/PromptConfirm';
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import { getResourceFilterFromScope } from '@app/shared/helpers/api';
import { showNotification } from '@mantine/notifications';
import { useParams } from 'react-router-dom';
import {
  ResourceAction,
  ResourceScope,
  ResourceStorageVariantEnum,
  ResourceStorageVariantPermissionMap,
} from '@app/types/blackroom-app';
import ResourceContextMenu from '@features/Resource/ResourceContextMenu';
import { useResourceFilteredActions } from '../../hooks/useResourceFilteredActions';
import useCanManageResources from '../../hooks/useCanManageResources';
import { PAGE_SIZE, ResourceColumn } from '../../ResourceTable/types';
import { useReactiveVar } from '@apollo/client';
import { resourceClipboardVar } from '../../store/copiedResources';
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import LabelStorageVariant from '@ui-kit/atoms/LabelStorageVariant';
import { useRepairTextResourcesAction } from './useRepairTextResourcesAction';

const sharedActionButtonProps: ButtonProps = {
  variant: 'light',
  color: 'blackroomBlack',
  bg: surfaces.sunken,
  c: palette.blackroomDarkGray4,
  px: 'xs',
  radius: 'sm',
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ActionBar({
  selectedResources: resources = [],
  scope = 'my',
  selectAll = false,
  table,
  setColumnVisibility,
  columnVisibility,
  requestVariables,
  ...props
}: ActionBarProps) {
  /* ********************************** HOOKS ********************************* */

  // Store
  const { isCurrentWorkspaceAdmin, hasPermission } = useAccount();
  const canSeeAllColumns =
    isCurrentWorkspaceAdmin || hasPermission('manageResources');
  const workspace = useWorkspace();
  const dialogStore = useReactiveVar(dialogStoreVar);
  const resourceClipboard = useReactiveVar(resourceClipboardVar);

  // Actions
  const { allowedActions } = useResourceFilteredActions({
    resources: resources,
    scope,
  });
  const actionDescriptors = pick(useResourceActions(), allowedActions);
  const repairTextAction = useRepairTextResourcesAction(scope);
  const RepairTextIcon = repairTextAction.iconComponent;

  // Localization
  const { t } = useTranslation(['Common', 'Resource', 'Settings']);

  // API
  const [reindexWorkspace] = useReindexWorkspaceMutation();

  // Routing
  const { folderId } = useParams<{
    scope: ResourceScope;
    folderId?: string;
  }>();
  /* ***************************** LOCAL VARIABLES **************************** */

  const { canManageResourcesHere } = useCanManageResources();

  const workspaceActions = canManageResourcesHere &&
    scope === 'my' &&
    resourceClipboard.resourceIds.length > 0 && (
      <Button
        {...sharedActionButtonProps}
        leftSection={<IconClipboard size={16} />}
        rightSection={<ShortcutKbd shortcut={isMacOS ? '⌘V' : 'Ctrl+V'} />}
        onClick={() => {
          dialogStore?.invokeDialog?.('dialog-resource-copy', {
            destinationFolderId: folderId,
            requestVariables,
            modalProps: {
              withCloseButton: false,
            },
          });
        }}
      >
        {t('Resource:Common.actions.paste')}
      </Button>
    );

  const reindexAction = canManageResourcesHere && scope === 'my' && (
    <Button
      {...sharedActionButtonProps}
      leftSection={<IconReindex size={16} />}
      onClick={() => {
        reindex();
      }}
    >
      {t('Resource:Common.actions.reindex')}
    </Button>
  );

  const mainActions = pick(actionDescriptors, ['download']);
  const secondaryActions = omit(actionDescriptors, ['download']);

  const resourceIds = resources.map((r) => r.id);
  const [singleResource] = resources;
  const singleResourceId = singleResource?.id;

  const columnsHiddenCount = Object.keys(columnVisibility).filter(
    (column) => columnVisibility[column as ResourceColumn] === false,
  ).length;

  /* ******************************** CALLBACKS ******************************* */

  const reindex = useCallback(() => {
    invokePromptConfirm({
      componentProps: {
        modalContent: (
          <Text>
            {t(
              'Settings:PageSettings.perspectives.general.sections.index.fields.reindex.action.confirmPrompt',
            )}
          </Text>
        ),
      },
      onConfirm: async () => {
        await reindexWorkspace({
          variables: {
            workspaceId: workspace.id,
            folderId: folderId ?? null,
          },
          refetchQueries: [
            {
              query: ListResourcesDocument,
              variables: {
                filter: {
                  parentId: folderId ?? null,
                  workspaceId: workspace.id,
                  ...getResourceFilterFromScope('my'),
                },
                sorter: { rank: SortDirection.Asc },
                limit: PAGE_SIZE,
                offset: 0,
              } as ListResourcesQueryVariables,
              fetchPolicy: 'network-only',
            },
          ],
        })
          .then(() => {
            showNotification({
              title: t(
                'Resource:Common.operation.reindex.action.notification.title',
              ),
              message: t(
                'Resource:Common.operation.reindex.action.notification.message',
              ),
              color: 'blackroomBlue',
            });
          })
          .catch(() => {
            showNotification({
              title: t('Common:errors.genericTitle'),
              message: t('Common:errors.genericMessage'),
              color: 'blackroomRed',
            });
          });
      },
    });
  }, [workspace.id, folderId]);

  const runResourceAction = useCallback(
    async (action: ActionSignature, downloadPermission?: Permission) => {
      await action({
        resources: resources,
        singleResource,
        resourceIds,
        singleResourceId,
        options: {
          selectAll,
          parentId: folderId,
          downloadPermission,
        },
      });
    },
    [
      folderId,
      resourceIds,
      resources,
      selectAll,
      singleResource,
      singleResourceId,
    ],
  );

  /* ******************************** RENDERING ******************************* */

  return (
    <Group {...props} gap={8}>
      {resources.length > 0 && (
        <>
          {Object.entries(mainActions).map(
            ([actionName, { label, iconComponent, action }]) => {
              const Icon = iconComponent;
              if (actionName === 'download' && isCurrentWorkspaceAdmin) {
                return (
                  <Menu
                    key={actionName}
                    position="bottom-start"
                    radius="sm"
                    shadow="sm"
                    withinPortal
                  >
                    <Menu.Target>
                      <Box>
                        <Button
                          {...sharedActionButtonProps}
                          leftSection={<Icon size={16} />}
                        >
                          {label}
                        </Button>
                      </Box>
                    </Menu.Target>

                    <Menu.Dropdown>
                      <Menu.Item
                        onClick={() =>
                          runResourceAction(
                            action,
                            ResourceStorageVariantPermissionMap[
                              ResourceStorageVariantEnum.original
                            ],
                          )
                        }
                      >
                        <LabelStorageVariant
                          storageVariant={ResourceStorageVariantEnum.original}
                        />
                      </Menu.Item>
                      <Menu.Item
                        onClick={() =>
                          runResourceAction(
                            action,
                            ResourceStorageVariantPermissionMap[
                              ResourceStorageVariantEnum.redacted
                            ],
                          )
                        }
                      >
                        <LabelStorageVariant
                          storageVariant={ResourceStorageVariantEnum.redacted}
                        />
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                );
              }

              return (
                <Button
                  key={actionName}
                  {...sharedActionButtonProps}
                  leftSection={<Icon size={16} />}
                  onClick={async () => {
                    await runResourceAction(action);
                  }}
                >
                  {label}
                </Button>
              );
            },
          )}
          <ResourceContextMenu
            resources={resources}
            allowedActions={Object.keys(secondaryActions) as ResourceAction[]}
            resetRowSelection={table?.resetRowSelection}
            requestVariables={requestVariables}
            actionOptions={{
              selectAll,
              parentId: folderId,
            }}
            target={
              <Button
                {...sharedActionButtonProps}
                leftSection={<IconSecondaryActions size={16} />}
              >
                {t('Resource:ResourcePane.toolbar.actions')}
              </Button>
            }
          />
          {repairTextAction.visible && (
            <Button
              {...sharedActionButtonProps}
              leftSection={<RepairTextIcon size={16} />}
              onClick={() =>
                repairTextAction.action({
                  resourceIds,
                  resetRowSelection: table?.resetRowSelection,
                })
              }
            >
              {repairTextAction.label}
            </Button>
          )}
        </>
      )}

      {workspaceActions}
      {reindexAction}

      <Menu
        position="bottom-end"
        radius="sm"
        shadow="lg"
        closeOnItemClick={false}
      >
        <Menu.Target>
          <Box>
            <Button
              variant="light"
              color="blackroomBlack"
              bg={surfaces.sunken}
              c={palette.blackroomDarkGray4}
              px="xs"
              radius="sm"
            >
              <Group gap={10} wrap="nowrap" align="center">
                <IconColumns size={20} />
                {columnsHiddenCount > 0 &&
                  t('Resource:Common.actions.columnsHidden', {
                    count: columnsHiddenCount,
                  })}
              </Group>
            </Button>
          </Box>
        </Menu.Target>

        <Menu.Dropdown miw={200}>
          <Menu.Label ta="center">
            {t('Support:Common.actions.showHideColumns')}
          </Menu.Label>
          {Object.keys(columnVisibility)
            .filter((column) => {
              if (!canSeeAllColumns && column === 'createdBy') {
                return false;
              }

              return column !== 'actions';
            })
            .map((column) => (
              <Menu.Item
                key={column}
                onClick={() => {
                  setColumnVisibility({
                    ...columnVisibility,
                    [column]: !columnVisibility[column as ResourceColumn],
                  });
                }}
              >
                <Group
                  gap={10}
                  align="center"
                  c={
                    columnVisibility[column as ResourceColumn] === false
                      ? palette.blackroomGray
                      : undefined
                  }
                >
                  {columnVisibility[column as ResourceColumn] === false ? (
                    <IconEyeOff size={18} color={palette.blackroomGray} />
                  ) : (
                    <IconEye size={18} color={palette.blackroomBlue} />
                  )}
                  {t(`Resource:ResourceTable.columns.${column}.label`)}
                </Group>
              </Menu.Item>
            ))}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}

export default ActionBar;
